import React from "react";
import { connect } from "react-redux";

import Checkbox from "antd/es/checkbox/Checkbox";
import { Col, Row } from "antd/es/grid";
import { useIntl } from "react-intl";

import EntityType from "@mapmycustomers/shared/enum/EntityType";
import { EntityTypesSupportedByIntegrations } from "@mapmycustomers/shared/types/entity";
import { IntegrationMetadata } from "@mapmycustomers/shared/types/integrations/IntegrationMetadata";

import { toggleFileSyncing, toggleNoteSyncing } from "@app/store/integrations/actions";
import {
  getIntegrationFileSyncing,
  getIntegrationNoteSyncing,
} from "@app/store/integrations/selectors";
import { RootState } from "@app/store/rootReducer";
import layout from "@app/styles/layout";
import { getEntityTypeDisplayName } from "@app/util/ui";

import styles from "./Steps.module.scss";

interface SyncOptionsProps {
  entityType: EntityTypesSupportedByIntegrations;
  fileSyncing: IntegrationMetadata["fileSyncing"];
  noteSyncing: IntegrationMetadata["noteSyncing"];
  serviceName: string;
  toggleFileSyncing: typeof toggleFileSyncing;
  toggleNoteSyncing: typeof toggleNoteSyncing;
}

const SyncOptions: React.FC<SyncOptionsProps> = ({
  entityType,
  fileSyncing,
  noteSyncing,
  serviceName,
  toggleFileSyncing,
  toggleNoteSyncing,
}) => {
  const intl = useIntl();

  return (
    <Row gutter={layout.spacerM}>
      {entityType !== EntityType.ACTIVITY && (
        <Col>
          <Checkbox
            checked={noteSyncing?.[entityType]}
            className={styles.syncToggler}
            onChange={() => toggleNoteSyncing(entityType)}
          >
            {intl.formatMessage(
              {
                id: "integrations.setupStep.syncNotes",
                defaultMessage: "Sync Notes on {entityType}",
                description: "Sync notes label for setup step",
              },
              {
                entityType: getEntityTypeDisplayName(intl, entityType, {
                  lowercase: false,
                  plural: true,
                }),
              }
            )}
          </Checkbox>
        </Col>
      )}
      <Col>
        <Checkbox
          checked={fileSyncing?.[entityType]}
          className={styles.syncToggler}
          onChange={() => toggleFileSyncing(entityType)}
        >
          {intl.formatMessage(
            {
              id: "integrations.setupStep.syncFiles",
              defaultMessage: "Sync Files on {entityType} (to {crmName} only)",
              description: "Sync files label for setup step",
            },
            {
              crmName: serviceName,
              entityType: getEntityTypeDisplayName(intl, entityType, {
                lowercase: false,
                plural: true,
              }),
            }
          )}
        </Checkbox>
      </Col>
    </Row>
  );
};

const mapStateToProps = (state: RootState) => ({
  fileSyncing: getIntegrationFileSyncing(state),
  noteSyncing: getIntegrationNoteSyncing(state),
});

const mapDispatchToProps = {
  toggleFileSyncing,
  toggleNoteSyncing,
};

export default connect(mapStateToProps, mapDispatchToProps)(SyncOptions);
